<template lang="pug">
.study-bulk-answer-horizontal
  .study-bulk-answer-horizontal__content(v-if='!isChangingProblem')
    template(v-for='(problem, pIndex) in studyProblems')
      .study-bulk-answer-horizontal__content__list(v-for='(question, qIndex) in problem.questions')
        //- 問題番号表示部
        .study-bulk-answer-horizontal__content__list__number.text-large
          .study-bulk-answer-horizontal__content__list__number__problem-number
            .study-bulk-answer-horizontal__content__list__number__problem-number__label
              slot(name='problemNumberBeforeLabel')
            .study-bulk-answer-horizontal__content__list__nunber__problem-number__value {{ problem.problemNumber }}
            .study-bulk-answer-horizontal__content__list__number__problem-number__label
              slot(name='problemNumberAfterLabel')
          .study-bulk-answer-horizontal__content__list__number__question-number {{ question.questionNumberText }}
        //- 解答入力部
        .study-bulk-answer-horizontal__content__list__answer
          //- 指示文
          .study-bulk-answer-horizontal__content__list__answer__question-text(
            v-html='question.indicativeText',
            v-if='question.indicativeText'
          )
          //- テキスト、テキストエリア、プルダウン、手書き/キーボード
          template(v-for='(data, index) in question.questionDatas', v-if='question.questionDatas')
            component.study-bulk-answer-horizontal__content__list__answer__input(
              :is='inputComponentType(question.answerFormat)',
              v-model='data.inputValue',
              :beforeText='data.beforeText',
              :afterText='data.afterText',
              :items='data.selectItems',
              :inputType='data.inputType',
              :lang='data.lang',
              :savedStrokes='data.savedStrokes',
              :answerFormat='question.answerFormat',
              :mathKeyboardName='mathKeyboardName',
              @set-input-type='setInputType($event, index, qIndex, pIndex)',
              @set-stroke-data='setStrokeData($event, index, qIndex, pIndex)'
            )
              template(#tegakiResultTitle)
                slot(name='tegakiResultTitle')
              template(#tegakiRecognizeButtonLabel)
                slot(name='tegakiRecognizeButtonLabel')

          //- 単一選択、複数選択、並び替え
          template(v-if='question.questionChoiceData')
            component.study-bulk-answer-horizontal__content__list__answer__choice(
              :is='choiceComponentType(question.answerFormat)',
              v-model='question.questionChoiceData.inputValue',
              :items='question.questionChoiceData.choiceItems',
              :labelNotDisplayable='question.questionChoiceData.labelNotDisplayable',
              :canDeselected='question.questionChoiceData.canDeselectedOrder'
            )
  //- 次へボタン
  button-next.study-bulk-answer-horizontal__button(v-if='showNextButton', @click='nextEvent')
    slot(name='nextButtonLabel')
  button-primary.study-bulk-answer-horizontal__button(v-else, @click='completeEvent')
    slot(name='completeButtonLabel')
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator'
import InputTextFieldHorizontal from '@/components/organisms/student/InputTextFieldHorizontal.vue'
import InputTextareaHorizontal from '@/components/organisms/student/InputTextareaHorizontal.vue'
import InputSingleChoice from '@/components/organisms/student/InputSingleChoice.vue'
import InputMultiChoice from '@/components/organisms/student/InputMultiChoice.vue'
import InputDialogHorizontal from '@/components/organisms/student/InputDialogHorizontal.vue'
import InputPulldown from '@/components/organisms/student/InputPulldown.vue'
import InputChangeOrder from '@/components/organisms/student/InputChangeOrder.vue'
import ButtonNext from '@/components/molecules/student/ButtonNext.vue'
import ButtonPrimary from '@/components/molecules/student/ButtonPrimary.vue'
import { StudyProblem, InputDialogType } from '@/models/studyModel'
import { StrokePoint } from '@/models/StrokePoint'
import { AnswerFormats, AnswerFormat } from '@/const/AnswerFormats'

/**
 * 学習画面一括解答入力エリア（横書き）
 */
@Component({
  components: {
    InputTextFieldHorizontal,
    InputTextareaHorizontal,
    InputSingleChoice,
    InputMultiChoice,
    InputDialogHorizontal,
    InputPulldown,
    InputChangeOrder,
    ButtonNext,
    ButtonPrimary,
  },
})
export default class StudyBulkAnswerHorizontal extends Vue {
  // 問題切替中フラグ
  @Prop()
  isChangingProblem!: boolean

  // 問題情報群
  @Prop()
  studyProblems!: StudyProblem[]

  // 数学キーボード名
  @Prop()
  mathKeyboardName?: string

  // 次へボタン表示フラグ
  @Prop()
  showNextButton!: boolean

  // 次へボタン押下時イベント
  @Prop()
  nextEvent?: Function

  // 完了ボタン押下時イベント
  @Prop()
  completeEvent!: Function

  // 入力系コンポーネント名取得
  private inputComponentType(answerFormat: AnswerFormat): string {
    // 手書き/ソフトウェアキーボード用
    if (!!answerFormat.isTegaki || !!answerFormat.isKeyboard) {
      return 'InputDialogHorizontal'
    }

    // それ以外は入力フォーマットに応じたコンポーネントを返す
    switch (answerFormat) {
      case AnswerFormats.TEXT:
        return 'InputTextFieldHorizontal'
      case AnswerFormats.TEXTAREA:
        return 'InputTextareaHorizontal'
      case AnswerFormats.MULTI_PULLDOWN:
        return 'InputPulldown'
    }
    return ''
  }

  // 選択系コンポーネント名取得
  private choiceComponentType(answerFormat: AnswerFormat): string {
    switch (answerFormat) {
      case AnswerFormats.SINGLE_CHOICE:
        return 'InputSingleChoice'
      case AnswerFormats.MULTI_CHOICE:
        return 'InputMultiChoice'
      case AnswerFormats.SORT_CHOICE:
        return 'InputChangeOrder'
    }
    return ''
  }

  // 入力方法が手書き/キーボードかをセット
  private setInputType(inputType: InputDialogType, index: number, questionIndex: number, problemIndex: number) {
    const questionDatas = this.studyProblems[problemIndex].questions[questionIndex].questionDatas
    if (questionDatas) {
      questionDatas[index].inputType = inputType
    }
  }

  // 手書きのストローク情報をセット
  private setStrokeData(strokes: StrokePoint[][], index: number, questionIndex: number, problemIndex: number) {
    const questionDatas = this.studyProblems[problemIndex].questions[questionIndex].questionDatas
    if (questionDatas) {
      questionDatas[index].savedStrokes = strokes
    }
  }
}
</script>

<style lang="stylus" scoped>
.study-bulk-answer-horizontal
  position relative
  border 2px solid var(--color-study-content__border)
  background-color var(--color-study-content__background)
  border-radius 4px
  padding 10px 20px 50px 20px

  &__content
    height 100%
    overflow-y auto

    &__list
      display flex
      padding 10px
      margin-bottom 5px
      &:not(:last-child)
        border-bottom 1px solid var(--color-study-content__border)

      &__number
        display flex
        flex 0.8

        &__problem-number
          display flex

      &__answer
        display flex
        flex-direction column
        width calc(90% - 100px)

        &__question-text
          margin-bottom 10px

        &__input
          margin-bottom 10px
          display flex
          align-items center
          flex-direction row
          flex-wrap wrap
          justify-content flex-start

  &__button
    position absolute
    bottom 10px
    right 40px
    width 180px
</style>
